import { RefinementCtx } from 'zod';
import { isAfter, addMinutes } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { EUROPE_UNOS_ID_REGEX, UNOS_ID_REGEX } from '@/constants/case/case.constants';
import { hasActiveTrialProgram } from '@/utils/helpers';
import { CaseStatus, NO_COORDINATOR_ID, ServiceType } from '@/api/apis/case';
import { EUROPE_REGION_ID } from '@/constants/serviceRegion.constants';
import { CaseFormType } from './schema';

export const validateDateInPast = <
  T extends Partial<CaseFormType> & { [key: string]: unknown },
  K extends keyof T & string,
>(
  formData: T,
  context: RefinementCtx,
  dateField: K,
  timeField: K,
  timeZoneField: K,
  requireMinimum5Minutes: boolean = false,
) => {
  const { reAllocationCaseId, serviceType, [dateField]: date, [timeField]: time, [timeZoneField]: timeZone } = formData;
  const allowPast = !!reAllocationCaseId || serviceType === ServiceType.DirectAcquisition;
  const dateTimeNotSpecified = !date || !time;
  if (allowPast || dateTimeNotSpecified) return;

  const pickerDateInTimezone = fromZonedTime(new Date(`${date}T${time}`), timeZone as string);
  const isInFutureOrNow = isAfter(pickerDateInTimezone, new Date()) || +pickerDateInTimezone === +new Date();
  if (!isInFutureOrNow) {
    context.addIssue({
      path: [dateField],
      message: 'The date cannot be in the past',
      code: 'custom',
    });
  }

  if (requireMinimum5Minutes && isInFutureOrNow) {
    const minimumFutureTime = addMinutes(new Date(), 5);
    if (!isAfter(pickerDateInTimezone, minimumFutureTime)) {
      context.addIssue({
        path: [dateField],
        message: 'The date must be at least 5 minutes in the future',
        code: 'custom',
      });
    }
  }
};

export const validateDateTimeSelection = <
  T extends Partial<CaseFormType> & { [key: string]: unknown },
  K extends keyof T & string,
>(
  formData: T,
  context: RefinementCtx,
  dateField: K,
  timeField: K,
) => {
  const date = formData[dateField];
  const time = formData[timeField];
  const dateAndTimeBothSelected = !!date && !!time;
  const nothingSelected = !time && !date;

  if (!dateAndTimeBothSelected && !nothingSelected) {
    context.addIssue({
      message: date ? 'Specify time' : 'Specify date',
      path: [date ? timeField : dateField],
      code: 'custom',
    });
  }
};

export const validateDonorOrRequired = (
  formData: Pick<CaseFormType, 'donorHospitalDate' | 'serviceType' | 'status'>,
  context: RefinementCtx,
) => {
  const { donorHospitalDate, serviceType, status } = formData;

  const isDaCase = serviceType === ServiceType.DirectAcquisition;
  const isDraftStatus = !status || status === CaseStatus.Draft || status === CaseStatus.TXCenterDraft;

  const donorOrRequiredByStatus = isDaCase || !isDraftStatus;

  if (donorOrRequiredByStatus && !donorHospitalDate) {
    context.addIssue({
      path: ['donorHospitalDate'],
      message: 'Select Donor Hospital Date',
      code: 'custom',
    });
  }
};

export const validateReallocation = (
  formData: Pick<CaseFormType, 'reAllocationCase' | 'reAllocationCaseId'>,
  context: RefinementCtx,
) => {
  if (formData.reAllocationCase && !formData.reAllocationCaseId) {
    context.addIssue({
      path: ['reAllocationCaseId'],
      message: 'Select Reallocation Case',
      code: 'custom',
    });
  }
};

export const validateTxCenterId = (
  formData: Pick<CaseFormType, 'isExpeditedCase' | 'opoRequest' | 'txCenterId'>,
  context: RefinementCtx,
  path: keyof CaseFormType,
  message: string,
) => {
  const { isExpeditedCase, opoRequest, txCenterId } = formData;
  if (!txCenterId && !opoRequest && !isExpeditedCase) {
    context.addIssue({
      path: [path],
      message,
      code: 'custom',
    });
  }
};

export const validateTxCenterCoordinator = (
  formData: Pick<CaseFormType, 'txCenterId' | 'coordinatorId' | 'serviceType'>,
  context: RefinementCtx,
) => {
  const { txCenterId, coordinatorId, serviceType } = formData;
  const coordinatorNotSelected = coordinatorId !== NO_COORDINATOR_ID && !coordinatorId;
  const isDaCase = serviceType === ServiceType.DirectAcquisition;
  if (txCenterId && coordinatorNotSelected && !isDaCase) {
    context.addIssue({
      path: ['coordinatorId'],
      message: 'Select TX Center Coordinator',
      code: 'custom',
    });
  }
};

export const validateDonorHospitalId = (
  formData: Pick<CaseFormType, 'serviceType' | 'donorHospitalId'>,
  context: RefinementCtx,
) => {
  const { serviceType, donorHospitalId } = formData;
  const isDaCase = serviceType === ServiceType.DirectAcquisition;
  if (!donorHospitalId && !isDaCase) {
    context.addIssue({
      path: ['donorHospitalId'],
      message: 'Select Donor Hospital',
      code: 'custom',
    });
  }
};

export const validateDonorHospitalAddress = (
  formData: Pick<CaseFormType, 'donorHospitalId' | 'hospitalCustomLocation'>,
  context: RefinementCtx,
) => {
  const { donorHospitalId, hospitalCustomLocation } = formData;
  if (donorHospitalId && !hospitalCustomLocation?.addressId) {
    context.addIssue({
      path: ['hospitalCustomLocation.addressId'],
      message: 'Select valid hospital address from the list',
      code: 'custom',
    });
  }
};

export const validateOpoId = (
  formData: Pick<CaseFormType, 'opoId' | 'serviceRegionId' | 'serviceType'>,
  context: RefinementCtx,
) => {
  const { serviceRegionId, opoId, serviceType } = formData;
  const isDaCase = serviceType === ServiceType.DirectAcquisition;
  const isEuropeanRegion = serviceRegionId === EUROPE_REGION_ID;
  if (!isEuropeanRegion && !isDaCase && !opoId) {
    context.addIssue({
      path: ['opoId'],
      message: 'Select OPO',
      code: 'custom',
    });
  }
};

export const validateOpoContact = (
  formData: Pick<CaseFormType, 'opoId' | 'opoRequest' | 'serviceRegionId' | 'opoContactId'>,
  context: RefinementCtx,
  path: keyof CaseFormType = 'opoContactId',
  message: string = 'Either OPO Contact or OPO request should be specified',
) => {
  const { opoRequest, opoId, opoContactId, serviceRegionId } = formData;
  const isEuropeRegion = serviceRegionId === EUROPE_REGION_ID;
  if (opoId && !opoContactId && !opoRequest && !isEuropeRegion) {
    context.addIssue({
      path: [path],
      message,
      code: 'custom',
    });
  }
};

export const validateTxCenterAddress = (
  formData: Pick<CaseFormType, 'txCenterId' | 'txCenterCustomLocation'>,
  context: RefinementCtx,
) => {
  const { txCenterId, txCenterCustomLocation } = formData;
  if (txCenterId && !txCenterCustomLocation?.addressId) {
    context.addIssue({
      path: ['txCenterCustomLocation.addressId'],
      message: 'Select valid address from the list',
      code: 'custom',
    });
  }
};

export const validateOpoRequestWithTrialTxCenter = (
  formData: Pick<CaseFormType, 'opoRequest' | 'txCenterTrialPrograms' | 'organType'>,
  context: RefinementCtx,
  path: string,
) => {
  const { opoRequest, txCenterTrialPrograms, organType } = formData;
  const txCenterHasTrial = hasActiveTrialProgram(organType, txCenterTrialPrograms);
  if (opoRequest && txCenterHasTrial) {
    context.addIssue({
      path: [path],
      message: '',
      code: 'custom',
    });
  }
};

export const validateDonorId = (
  formData: Pick<CaseFormType, 'serviceRegionId' | 'donorId'>,
  context: RefinementCtx,
) => {
  const { serviceRegionId, donorId } = formData;
  const isEuropeanRegion = serviceRegionId === EUROPE_REGION_ID;
  const regex = isEuropeanRegion ? EUROPE_UNOS_ID_REGEX : UNOS_ID_REGEX;
  const isValidDonorId = regex.test(donorId);
  if (!isValidDonorId) {
    context.addIssue({
      path: ['donorId'],
      message: isEuropeanRegion ? 'Invalid donor ID: must be up to 10 alphanumeric characters.' : 'Invalid UNOS ID',
      code: 'custom',
    });
  }
};

export const validateDirectAcquisitionRequiredFields = (
  formData: Pick<CaseFormType, 'donorHospitalId' | 'opoId' | 'serviceType' | 'serviceRegionId'>,
  context: RefinementCtx,
) => {
  const { donorHospitalId, opoId, serviceType, serviceRegionId } = formData;
  const isDaCase = serviceType === ServiceType.DirectAcquisition;

  const shouldRequireOpo = serviceRegionId !== EUROPE_REGION_ID;
  const isOpoMissingWhenRequired = shouldRequireOpo && !opoId;
  const isDonorHospitalMissing = !donorHospitalId;

  if (!isDaCase && (isDonorHospitalMissing || isOpoMissingWhenRequired)) {
    context.addIssue({
      path: ['serviceType'],
      message: 'Please specify both Donor Hospital and OPO to proceed with the NOP Service update',
      code: 'custom',
    });
  }
};

export const validateOpoContactPhone = (
  formData: Pick<CaseFormType, 'opoContactId' | 'opoSourceContactPhone' | 'secondaryOpoContactPhone'>,
  context: RefinementCtx,
  phoneProperty: keyof Pick<CaseFormType, 'opoContactId' | 'opoSourceContactPhone' | 'secondaryOpoContactPhone'>,
) => {
  const { opoContactId } = formData;

  if (opoContactId && !formData[phoneProperty]) {
    context.addIssue({
      path: [phoneProperty],
      message: 'Invalid phone number',
      code: 'custom',
    });
  }
};
